import {
    CollisionEventType,
    Component,
    ForceMode,
    Object3D,
    PhysXComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';
import {Vignette} from './vignette.js';

const tempVec = vec3.create();
const tempVec2 = vec3.create();

const AxisY = [0, 1, 0];

/** Smooth locomotion character controller for VR. */
export class SmoothLocomotion extends Component {
    static TypeName = 'smooth-locomotion';

    /** Player object which is moved */
    @property.object({required: true})
    player!: Object3D;

    /** Head object from which to get movement direction */
    @property.object()
    head!: Object3D | null;

    @property.object()
    nonVrCamera!: Object3D | null;

    @property.object()
    vignette!: Object3D | null;

    /** Movement speed, default `1.0` */
    @property.float(1.0)
    moveSpeed = 1.0;

    /** Allow flying (if false, will not move on the Y axis) */
    @property.bool(false)
    allowFly = false;

    /** Incremements to snap to for snap rotation, default `45` */
    @property.int(45)
    snapDegrees = 45;

    justSnapped = false;

    // Cannot snap turn again unless you go below this threshold
    snapDeadzone = 0.8;
    min = -4;
    max = 4;

    grabbing = false;

    touchingFloor = false;

    jumpPressed = false;

    physics!: PhysXComponent;
    start() {
        this.physics = this.object
            .findByNameDirect('PlayerCollider')[0]
            .getComponent(PhysXComponent)!;
        this.physics?.onCollision((t) => {
            this.touchingFloor = t === CollisionEventType.Touch;
        });
    }

    onActivate(): void {
        this.physics.object.setPositionLocal([0, 0.75, 0]);
        this.physics.active = true;

        window.addEventListener('keydown', this.press);
        window.addEventListener('keyup', this.release);
    }

    onDeactivate(): void {
        this.physics.active = false;

        window.removeEventListener('keydown', this.press);
        window.removeEventListener('keyup', this.release);
    }

    _direction = new Float32Array(3);

    update(dt: number) {
        const s = this.engine.xr?.session;
        if (s) this.updateVRControls(s);
        if (!this.physics) return;

        const dir = this._direction;
        const l = vec3.length(dir);
        const invL = l != 0 ? 1 / l : 0;
        vec3.scale(tempVec2, dir, this.moveSpeed * invL);

        tempVec2[1] = this.physics.linearVelocity[1];
        this.physics.linearVelocity = tempVec2;

        this.physics!.object.getPositionWorld(tempVec);
        tempVec[1] -= 0.75;
        this.player.setPositionWorld(tempVec);

        if (s && this.vignette) {
            this.physics.getLinearVelocity(tempVec2);
            this.vignette
                ?.getComponent(Vignette)
                ?.setMoving(vec3.squaredLength(tempVec2) > 0.1);
        }
    }

    updateVRControls(s: XRSession) {
        /* If there is no input, stop moving */
        vec3.zero(this._direction);

        /* Handle VR input */
        for (let i = 0; i < s.inputSources.length; ++i) {
            const input = s.inputSources[i];
            if (input.hand) continue;

            /* Snap rotate on right controller */
            const rotate = input.handedness == 'right';
            const gamepad = input.gamepad;
            if (!gamepad) continue;
            if (!gamepad.axes) continue;

            // Gather input from controller
            const xAxis = gamepad.axes[2];
            const yAxis = gamepad.axes[3];
            const gripped = gamepad.buttons[1]?.pressed;

            if (input.handedness == 'right') {
                const pressed = gamepad.buttons[4]?.pressed;
                if (!this.jumpPressed && pressed && this.touchingFloor) {
                    this.jump();
                    this.jumpPressed = true;
                } else if (!pressed) {
                    this.jumpPressed = false;
                }
            }

            // Handle movement and rotation
            if (rotate) {
                this.rotate(xAxis);
            } else {
                const direction = vec3.set(this._direction, xAxis, 0, yAxis);

                this.head?.transformVectorWorld(direction, direction);
                if (!this.allowFly) direction[1] = 0;
            }

            // Handle button pressed
            if (gripped && !this.grabbing) {
                this.grab();
                this.grabbing = true;
            } else if (this.grabbing) {
                this.drop();
                this.grabbing = false;
            }
        }
    }

    rotate(xAxis: number) {
        if (!this.head) return;

        /* Snap rotation */
        if (Math.abs(xAxis) > this.snapDeadzone && !this.justSnapped) {
            const lastHeadPos = this.head.getPositionWorld(tempVec);

            this.player.rotateAxisAngleDegLocal(
                AxisY,
                this.snapDegrees * -Math.sign(xAxis)
            );
            this.justSnapped = true;

            const curHeadPos = this.head.getPositionWorld(tempVec2);

            /* Move player such that head stays at same position */
            const newPos = vec3.sub(tempVec, lastHeadPos, curHeadPos);
            this.player.translateLocal(newPos);
        } else if (Math.abs(xAxis) < this.snapDeadzone) {
            this.justSnapped = false;
        }
    }

    jump() {
        this.physics?.addForce([0, 5 * this.physics.mass, 0], ForceMode.Impulse);
    }

    up = false;
    right = false;
    down = false;
    left = false;

    press = (e: KeyboardEvent) => {
        if (
            e.keyCode === 38 /* up */ ||
            e.keyCode === 87 /* w */ ||
            e.keyCode === 90 /* z */
        ) {
            this.up = true;
        } else if (e.keyCode === 39 /* right */ || e.keyCode === 68 /* d */) {
            this.right = true;
        } else if (e.keyCode === 40 /* down */ || e.keyCode === 83 /* s */) {
            this.down = true;
        } else if (
            e.keyCode === 37 /* left */ ||
            e.keyCode === 65 /* a */ ||
            e.keyCode === 81 /* q */
        ) {
            this.left = true;
        }

        if (this.touchingFloor && (e.key === ' ' || e.code === 'Space')) {
            this.jump();
        }

        this.updateDirection();
    };

    release = (e: KeyboardEvent) => {
        if (
            e.keyCode === 38 /* up */ ||
            e.keyCode === 87 /* w */ ||
            e.keyCode === 90 /* z */
        ) {
            this.up = false;
        } else if (e.keyCode === 39 /* right */ || e.keyCode === 68 /* d */) {
            this.right = false;
        } else if (e.keyCode === 40 /* down */ || e.keyCode === 83 /* s */) {
            this.down = false;
        } else if (
            e.keyCode === 37 /* left */ ||
            e.keyCode === 65 /* a */ ||
            e.keyCode === 81 /* q */
        ) {
            this.left = false;
        }

        this.updateDirection();
    };

    updateDirection() {
        const dir = this._direction;
        vec3.zero(dir);

        if (this.up) dir[2] -= 1.0;
        if (this.down) dir[2] += 1.0;
        if (this.left) dir[0] -= 1.0;
        if (this.right) dir[0] += 1.0;

        this.nonVrCamera?.transformVectorWorld(dir, dir);
        if (!this.allowFly) dir[1] = 0;
        vec3.normalize(dir, dir);
    }

    grab() {}

    drop() {}
}
